import { useState } from "react";
import { Clock, Package, Truck, ChevronDown, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";


interface SimpleGroupBuyDisplayProps {
  currentQuantity: number;
  targetQuantity: number;
  endDate?: string | null;
  currentPrice: number;
  measurementUnit: string;
  deliveryDays?: number;
  className?: string;
}

export const SimpleGroupBuyDisplay = ({
  currentQuantity,
  targetQuantity,
  endDate,
  currentPrice,
  measurementUnit,
  deliveryDays,
  className
}: SimpleGroupBuyDisplayProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const progressPercent = Math.min((currentQuantity / targetQuantity) * 100, 100);
  const daysLeft = endDate ? Math.ceil((new Date(endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
  const isGoalReached = progressPercent >= 100;

  const formatUnit = (unit: string) => {
    const units: Record<string, string> = {
      'square_meter': 'מ"ר',
      'meter': 'מטר',
      'unit': 'יח\'',
      'cubic_meter': 'מ"ק',
      'kilogram': 'ק"ג',
      'liter': 'ליטר',
      'package_by_meter': 'מטר',
      'package_by_square_meter': 'מ"ר'
    };
    return units[unit] || unit;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Main Status Card */}
      <Card className="p-6 space-y-4 bg-white border-2 border-amber-200">
        <div className="flex items-center justify-between border-b border-amber-100 pb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-lg text-amber-900">קבוצת רכישה</h3>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{daysLeft !== null && daysLeft > 0 ? `${daysLeft} ימים נותרו` : daysLeft === null ? 'ללא מגבלת זמן' : 'מסתיים היום'}</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">התקדמות</span>
            <span className="font-semibold">
              {currentQuantity.toFixed(1)} / {targetQuantity.toFixed(1)} {formatUnit(measurementUnit)}
            </span>
          </div>
          <Progress
            value={progressPercent}
            className={cn(
              "h-5 bg-amber-100/50 border border-amber-100", // Thicker and distinct background
              isGoalReached && "[&>div]:bg-gradient-to-r [&>div]:from-amber-500 [&>div]:via-yellow-400 [&>div]:to-amber-500"
            )}
          />
          <p className={cn(
            "text-xs text-center font-semibold",
            isGoalReached ? "text-amber-600" : "text-muted-foreground"
          )}>
            {isGoalReached
              ? "היעד הושג! הזמנות ימשיכו להתקבל עד לתאריך הסיום"
              : `${progressPercent.toFixed(0)}% הושלמו עד כה`
            }
          </p>
        </div>
      </Card>

      {/* Details Card */}
      <Card className="border border-amber-100 shadow-sm overflow-hidden">
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full flex items-center justify-between p-4 h-auto hover:bg-amber-50/50 hover:text-amber-700 rounded-none group"
            >
              <span className="text-sm font-medium text-muted-foreground group-hover:text-amber-700 transition-colors">
                {isOpen ? 'הסתר פרטים' : 'מהי קבוצת רכישה? / פרטים מלאים'}
              </span>
              <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:text-amber-700", isOpen && "rotate-180")} />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <div className="p-4 pt-0 space-y-4 bg-amber-50/30">

              {/* Delivery Info */}
              {deliveryDays && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t border-amber-100/50">
                  <Truck className="w-4 h-4" />
                  <span>אספקה משוערת: תאריך הגעה ליעד + {deliveryDays} ימים</span>
                </div>
              )}

              {/* Explanation */}
              <div className="text-sm space-y-3">
                <p className="font-medium text-amber-900">מהי קבוצת רכישה?</p>
                <p className="text-amber-900/80 leading-relaxed">
                  התאגדות צרכנים לרכישת כמות גדולה יחד במחיר מוזל במיוחד.
                </p>
                <div className="space-y-1 text-amber-900/70">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>המוצר יסופק רק בהגעה ליעד</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>החזר מלא אם לא נגיע ליעד</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>המוצר הינו מוצר קבוצת רכישה ועל כן לא ניתן להחזירו</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>אספקה משוערת: {deliveryDays ? `${deliveryDays} ימים` : '30-90 יום'} לאחר הגעה ליעד</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
};